import {
    REMOVE_CUSTOM_COLUMNS_GLOBAL,
    REMOVE_CUSTOM_COLUMNS_LOCAL,
    RESET_CUSTOM_COLUMNS_GLOBAL,
    RESET_CUSTOM_COLUMNS_LOCAL,
    SET_CUSTOM_COLUMNS_GLOBAL,
    SET_CUSTOM_COLUMNS_LOCAL,
    ADD_CUSTOM_METRIC,
    EDIT_CUSTOM_METRIC,
    DELETE_CUSTOM_METRIC,
    EXPAND_CUSTOM_METRIC,
    UPDATE_DRAFT_METRIC,
    CLEAR_DRAFT_METRIC,
    CLEAR_ALL_CUSTOM_METRICS_ON_DATAMART_CHANGE,
    START_EDITING_CUSTOM_METRIC,
    SAVE_EDITED_CUSTOM_METRIC,
    CANCEL_EDITING_CUSTOM_METRIC,
} from 'src/reduxActions/actionNameEnums';


export interface AddedMetric {
    id: string;
    name: string;
    prettyName: string;
    type: string;
    sql: string;
    isExpanded: boolean;
    isEditing?: boolean;
}

export interface DraftMetric {
    id: string;
    prettyName: string;
    type: string;
    sql: string;
    name?: string;
}

export interface CustomMetricsScope {
    added: { [id: string]: AddedMetric };
    draft: DraftMetric | null;
}

export interface CustomMetricsState {
    global: CustomMetricsScope;
    local: CustomMetricsScope;
}

const initialState: CustomMetricsState = {
    global: { added: {}, draft: null },
    local: { added: {}, draft: null },
};


const migrateStateToNewCustomMetricsFormat = (existingState: any): CustomMetricsState => {
    if (!existingState) {
        return initialState;
    }

    if (existingState.global && typeof existingState.global === 'object') {
        const hasNewFormat = existingState.global.added !== undefined || existingState.global.draft !== undefined;
        
        if (!hasNewFormat) {
            return {
                global: {
                    added: existingState.global || {},
                    draft: null,
                },
                local: {
                    added: existingState.local || {},
                    draft: null,
                },
            };
        }
    }
    
    return existingState;
};

const getScope = (state: CustomMetricsState, customColumnType: string): CustomMetricsScope => {
    return state[customColumnType as keyof CustomMetricsState] as CustomMetricsScope;
};

const updateScope = (
    state: CustomMetricsState,
    customColumnType: string,
    updater: (scope: CustomMetricsScope) => CustomMetricsScope,
): CustomMetricsState => {
    const scope = getScope(state, customColumnType);
    return {
        ...state,
        [customColumnType]: updater(scope),
    };
};

const updateScopeAdded = (
    state: CustomMetricsState,
    customColumnType: string,
    updater: (added: { [id: string]: AddedMetric }) => { [id: string]: AddedMetric },
): CustomMetricsState => {
    return updateScope(state, customColumnType, (scope) => ({
        ...scope,
        added: updater(scope.added),
    }));
};

const updateScopeDraft = (
    state: CustomMetricsState,
    customColumnType: string,
    draft: DraftMetric | null,
): CustomMetricsState => {
    return updateScope(state, customColumnType, (scope) => ({
        ...scope,
        draft,
    }));
};

const customMetricesReducer = (state = initialState, action: any): CustomMetricsState => {
    const currentState = migrateStateToNewCustomMetricsFormat(state);
    const { type, payload } = action;
    
    switch(type) {

        case SET_CUSTOM_COLUMNS_GLOBAL:
            return {
                ...currentState,
                global: {
                    ...currentState.global,
                    added: {
                        ...currentState.global.added,
                        [payload.name]: payload.body,
                    },
                },
            };
            
        case SET_CUSTOM_COLUMNS_LOCAL: {
            const localAdded = { ...currentState.local.added };
            if (Array.isArray(payload)) {
                payload.forEach((item: any) => {
                    localAdded[item.name] = item.body;
                });
            } else {
                localAdded[payload.name] = payload.body;
            }
            return {
                ...currentState,
                local: {
                    ...currentState.local,
                    added: localAdded,
                },
            };
        }
        
        case REMOVE_CUSTOM_COLUMNS_GLOBAL: {
            const { [payload.name]: removed, ...remaining } = currentState.global.added;
            return {
                ...currentState,
                global: {
                    ...currentState.global,
                    added: remaining,
                },
            };
        }
        
        case REMOVE_CUSTOM_COLUMNS_LOCAL: {
            const { [payload.name]: removed, ...remaining } = currentState.local.added;
            return {
                ...currentState,
                local: {
                    ...currentState.local,
                    added: remaining,
                },
            };
        }
        
        case RESET_CUSTOM_COLUMNS_GLOBAL:
            return {
                ...currentState,
                global: {
                    added: payload || {},
                    draft: null,
                },
            };
            
        case RESET_CUSTOM_COLUMNS_LOCAL:
            return {
                ...currentState,
                local: {
                    added: payload || {},
                    draft: null,
                },
            };
            
        // New enhanced custom metrics actions
        case ADD_CUSTOM_METRIC: {
            const { customColumnType, metric } = payload;
            return updateScope(currentState, customColumnType, (scope) => ({
                ...scope,
                added: {
                    ...scope.added,
                    [metric.id]: { ...metric, isExpanded: false },
                },
                draft: null,
            }));
        }
        
        case EDIT_CUSTOM_METRIC: {
            const { customColumnType, metricId, updates } = payload;
            return updateScopeAdded(currentState, customColumnType, (added) => ({
                ...added,
                [metricId]: {
                    ...added[metricId],
                    ...updates,
                },
            }));
        }
        
        case DELETE_CUSTOM_METRIC: {
            const { customColumnType, metricId } = payload;
            return updateScopeAdded(currentState, customColumnType, (added) => {
                const { [metricId]: removed, ...remaining } = added;
                return remaining;
            });
        }
        
        case EXPAND_CUSTOM_METRIC: {
            const { customColumnType, metricId } = payload;
            const scope = getScope(currentState, customColumnType);
            const metric = scope.added[metricId];
            if (!metric) return currentState;
            
            return updateScopeAdded(currentState, customColumnType, (added) => ({
                ...added,
                [metricId]: {
                    ...added[metricId],
                    isExpanded: !added[metricId].isExpanded,
                },
            }));
        }
        
        case START_EDITING_CUSTOM_METRIC: {
            const { customColumnType, metricId } = payload;
            return updateScopeAdded(currentState, customColumnType, (added) => ({
                ...added,
                [metricId]: {
                    ...added[metricId],
                    isEditing: true,
                    isExpanded: true,
                },
            }));
        }

        case SAVE_EDITED_CUSTOM_METRIC: {
            const { customColumnType, metricId, updates } = payload;
            return updateScopeAdded(currentState, customColumnType, (added) => ({
                ...added,
                [metricId]: {
                    ...added[metricId],
                    ...updates,
                    isEditing: false,
                },
            }));
        }

        case CANCEL_EDITING_CUSTOM_METRIC: {
            const { customColumnType, metricId } = payload;
            return updateScopeAdded(currentState, customColumnType, (added) => ({
                ...added,
                [metricId]: {
                    ...added[metricId],
                    isEditing: false,
                },
            }));
        }
        
        case UPDATE_DRAFT_METRIC: {
            const { customColumnType, draft } = payload;
            return updateScopeDraft(currentState, customColumnType, draft);
        }
        
        case CLEAR_DRAFT_METRIC: {
            const { customColumnType } = payload;
            return updateScopeDraft(currentState, customColumnType, null);
        }
        
        case CLEAR_ALL_CUSTOM_METRICS_ON_DATAMART_CHANGE:
            return {
                global: { added: {}, draft: null },
                local: { added: {}, draft: null },
            };
            
        default:
            return currentState;
    }
};

export default customMetricesReducer;
